import React, { useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'

import PhoneNumberField from '@/components/formik/fields/PhoneNumberField'
import TextField from '@/components/formik/fields/TextField'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'

export type ParentDialogProps = {
  initialValues: PatientProfileFormData['parents'][number]
  mode: 'create' | 'update'
  onClose?: () => void
  onSubmit: (values: PatientProfileFormData['parents'][number]) => void
  open: boolean
  readOnly?: boolean
}

export function ParentDialog(props: ParentDialogProps) {
  const { initialValues, mode, onClose, onSubmit, open, readOnly = false } = props
  const { t } = useTranslation()

  const [editable, setEditable] = useState<boolean>(mode === 'create' && !readOnly)

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        firstname: yup.string().required(),
        lastname: yup.string().required(),
        email: yup.string().email(),
        city: yup.string(),
        cp: yup.number(),
      }),
    []
  )

  const toggleEditable = () => {
    setEditable(!editable)
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>{t('page.infoPatient.parentDialog.title', { context: mode })}</DialogTitle>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={t('field.lastName.label')} name="lastname" readOnly={!editable} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={t('field.firstName.label')} name="firstname" readOnly={!editable} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={t('field.email.label')} name="email" readOnly={!editable} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneNumberField label={t('field.phone.label')} name="phone" readOnly={!editable} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={t('field.city.label')} name="city" readOnly={!editable} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label={t('field.postalCode.label')} name="cp" readOnly={!editable} />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} variant="neutral">
                {t('common:button.close.label')}
              </Button>
              {mode === 'create' && (
                <Button type="submit" variant="contained">
                  {t('common:button.add.label')}
                </Button>
              )}
              {mode === 'update' && !editable && !readOnly && (
                <Button onClick={toggleEditable} variant="contained">
                  {t('common:button.modify.label')}
                </Button>
              )}
              {mode === 'update' && editable && (
                <Button type="submit" variant="contained">
                  {t('common:button.submit.label')}
                </Button>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default ParentDialog
